import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CALL_STATUS } from "./constants";
import mediaAPI from "../../api/media";

const initialState = {
	status: CALL_STATUS.IDLE,
	error: null,
	points: []
};

export const getBoundingMediaPoints = createAsyncThunk(
	'media/GET_BOUNDING_MEDIA_POINTS',
	async (bounds) => {
		const response = await mediaAPI.getBoundingMediaPoints(bounds);
		return response;
	}
);

export const getBoundingMediaPointsAuth = createAsyncThunk(
	'media/GET_BOUNDING_MEDIA_POINTS_AUTH',
	async ({ bounds, token }) => {
		const response = await mediaAPI.getBoundingMediaPointsAuth(bounds, token);
		return response;
	}
);

export const likeMedia = createAsyncThunk('media/likeMedia',
	async ({ mediaId, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.likeMedia(mediaId, token);
			return mediaId;
		} catch (error) {
			return rejectWithValue(mediaId);
		}
	}
);

export const unlikeMedia = createAsyncThunk('media/unlikeMedia',
	async ({ mediaId, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.unlikeMedia(mediaId, token);
			return mediaId;
		} catch (error) {
			return rejectWithValue(mediaId);
		}
	}
);

export const deleteMedia = createAsyncThunk('media/deleteMedia',
	async ({ mediaId, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.deleteMedia(mediaId, token);
			return mediaId;
		} catch (error) {
			console.error("Failed to delete media:", error);
			return rejectWithValue(error.message);
		}
	}
);

export const reportMedia = createAsyncThunk('media/reportMedia',
	async ({ mediaId, reason, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.reportMedia(mediaId, reason, token);
			return { mediaId, reason };
		} catch (error) {
			console.error("Failed to report media:", error);
			return rejectWithValue(error.message);
		}
	}
);

export const blockMedia = createAsyncThunk('media/blockMedia',
	async ({ mediaId, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.blockMedia(mediaId, token);
			return mediaId;
		} catch (error) {
			console.error("Failed to block media:", error);
			return rejectWithValue(error.message);
		}
	}
);

export const unblockMedia = createAsyncThunk('media/unblockMedia',
	async ({ mediaId, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.unblockMedia(mediaId, token);
			return mediaId;
		} catch (error) {
			console.error("Failed to unblock media:", error);
			return rejectWithValue(error.message);
		}
	}
);

export const blockUser = createAsyncThunk('media/blockUser',
	async ({ userId, blockVideos, token }, { rejectWithValue }) => {
		try {
			await mediaAPI.blockUser(userId, blockVideos, token);
			return userId;
		} catch (error) {
			console.error("Failed to block user:", error);
			return rejectWithValue(error.message);
		}
	}
);


const mediaSlice = createSlice({
	name: "media",
	initialState,
	reducers: {
		reset: () => initialState
	},
	extraReducers: (builder) => {
		builder
			.addCase(getBoundingMediaPoints.pending, (state, action) => {
				state.status = CALL_STATUS.PENDING;
				state.error = null;
			})
			.addCase(getBoundingMediaPoints.fulfilled, (state, action) => {
				state.status = CALL_STATUS.SUCCESS;
				state.error = null;
				state.points = action.payload.data;
			})
			.addCase(getBoundingMediaPoints.rejected, (state, action) => {
				state.status = CALL_STATUS.ERROR;
				state.error = null;
			})
			.addCase(getBoundingMediaPointsAuth.pending, (state, action) => {
				state.status = CALL_STATUS.PENDING;
				state.error = null;
			})
			.addCase(getBoundingMediaPointsAuth.fulfilled, (state, action) => {
				state.status = CALL_STATUS.SUCCESS;
				state.error = null;
				state.points = action.payload.data;
			})
			.addCase(getBoundingMediaPointsAuth.rejected, (state, action) => {
				state.status = CALL_STATUS.ERROR;
				state.error = null;
			})
			.addCase(likeMedia.pending, (state, action) => {
				const point = state.points.find(p => p.id === action.meta.arg.mediaId);
				if (point) {
					point.likedByCurrentUser = true;
					point.likesCount += 1;
				}
			})
			.addCase(likeMedia.rejected, (state, action) => {
				const point = state.points.find(p => p.id === action.payload);
				if (point) {
					point.likedByCurrentUser = false;
					point.likesCount = Math.max(0, point.likesCount - 1);
				}
			})
			.addCase(unlikeMedia.pending, (state, action) => {
				const point = state.points.find(p => p.id === action.meta.arg.mediaId);
				if (point) {
					point.likedByCurrentUser = false;
					point.likesCount = Math.max(0, point.likesCount - 1);
				}
			})
			.addCase(unlikeMedia.rejected, (state, action) => {
				const point = state.points.find(p => p.id === action.payload);
				if (point) {
					point.likedByCurrentUser = true;
					point.likesCount += 1;
				}
			})
			.addCase(deleteMedia.fulfilled, (state, action) => {
				state.points = state.points.filter(p => p.id !== action.payload);
			})
			.addCase(blockMedia.fulfilled, (state, action) => {
				state.points = state.points.filter(p => p.id !== action.payload);
			})
			.addCase(blockUser.fulfilled, (state, action) => {
				if (action.meta.arg.blockVideos) {
					const userId = action.payload;
					state.points = state.points.filter(p => p.ownerId !== userId);
				}
			});
	}
});

const { actions, reducer } = mediaSlice;

export const {
	reset
} = actions;

export default reducer;